import { useState, useEffect } from 'react';
import { createVirtualRoom, getEnvironments, getAccounts, generateDeepLink } from '../services/api';
import { VirtualRoomRequest, VirtualRoomResponse, BytePlusConfig, Account } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

const VirtualRoomCreator = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VirtualRoomResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [titleSuffix, setTitleSuffix] = useState('Testing');
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [configType] = useState('Provider'); // Default
  const [titlePrefix] = useState('[Provider]'); // Default
  const [roomHistory, setRoomHistory] = useState<VirtualRoomResponse[]>([]);

  // Deep Link 相關狀態
  const [deepLinks, setDeepLinks] = useState<Record<string, string>>({});
  const [loadingDeepLinks, setLoadingDeepLinks] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // BytePlus 預設配置（基於 meeting_configs.yaml）- 完整 30+ 個選項
  const [meetingConfig, setMeetingConfig] = useState<BytePlusConfig>({
    // 基礎通訊功能
    enableChat: true,
    enableRecording: true,
    enableFilePlayer: true,
    enableWhiteBoard: true,
    enableContentShare: true,

    // 互動工具
    enableQuiz: true,
    enablePoll: true,
    enableReaction: true,
    enableReward: true,

    // 多媒體工具
    enableYoutubePlayer: true,
    enableWebLinks: true,

    // 輔助工具
    enableCountdown: true,
    enableNameRandomizer: true,
    enableDice: true,
    enableCalculator: true,
    enableCodeEditor: false,

    // 第三方整合
    enableNearpod: true,

    // 進階功能
    enableBreakoutGroup: false,
    enableStudentScreenViewer: false,
    enableStudentSendChatFile: false,
    enablePostClassTranscription: false,
    enableVirtualBackground: true,
    enableAfterClassEval: true,
    enableWaitingRoom: false,

    // 錄影相關
    enableRecordingButton: true,
    enableAutoRecording: false,
    enableRecordingPermission: true,
    enableBrowserRecBanner: true,
    enablePrivacyRecording: false,
    recordingMethod: 'CLASSIC',
    recordingFileTypes: ['MP4'],

    // 課程管理
    enableLessonPlan: false,
    enableClassroomInvitation: true,
    enableEndButton: true,
    enableStartStopClass: false,
    enableSupportTicket: false,
    enableTeacherOnboarding: true,

    // 系統設定
    enableBrowserUpdatePrompt: false,
    defaultView: 'sidebar',
  });

  // 獲取用戶顯示名稱（用於會議標題）
  const getUserDisplayName = () => {
    if (!user?.name) return 'Febe';

    // 如果名字包含 Chying，預設使用 Febe
    if (user.name.toLowerCase().includes('chying')) {
      return 'Febe';
    }

    // 取名字的第一個字（假設中文名字格式）
    // 如果是兩個字以上，取第一個字
    const trimmedName = user.name.trim();
    if (trimmedName.length >= 2) {
      return trimmedName.charAt(1); // 跳過姓氏，取名字第一個字
    }

    return trimmedName.charAt(0) || 'Febe';
  };

  const [formData, setFormData] = useState<VirtualRoomRequest>({
    name: getUserDisplayName(),
    description: '',
    maxParticipants: 10,
    duration: 60,
    features: [],
    environment: 'dev',
    provider: 'byteplus',
    timePreset: 'now',
  });

  // 加載環境和帳號數據，以及歷史記錄
  useEffect(() => {
    loadEnvironments();
    loadRoomHistory(); // 載入歷史記錄
  }, []);

  useEffect(() => {
    if (formData.environment) {
      loadAccounts();
    }
  }, [formData.environment]);

  const loadEnvironments = async () => {
    try {
      const envs = await getEnvironments();
      if (envs) {
        setEnvironments(envs);
      }
    } catch (error: any) {
      console.error('加載環境失敗:', error);
      setError('加載環境配置失敗: ' + error.message);
    }
  };

  const loadAccounts = async () => {
    if (!formData.environment) return;
    try {
      const fetchedAccounts = await getAccounts(formData.environment);
      if (fetchedAccounts) {
        setAccounts(fetchedAccounts);
        // 自動選擇 byteplus 帳號
        const byteplusAccount = fetchedAccounts.find((acc) => acc.key.includes('byteplus'));
        if (byteplusAccount) {
          setSelectedAccount(byteplusAccount.key);
          setFormData(prev => ({ ...prev, account: byteplusAccount.key }));
        } else if (fetchedAccounts.length > 0) {
          setSelectedAccount(fetchedAccounts[0].key);
          setFormData(prev => ({ ...prev, account: fetchedAccounts[0].key }));
        } else {
          setSelectedAccount(null);
        }
      }
    } catch (error: any) {
      console.error('加載帳號失敗:', error);
      setError('加載帳號失敗: ' + error.message);
    }
  };

  const getFinalTitlePreview = () => {
    const userName = formData.name || getUserDisplayName();
    const suffixPart = titleSuffix ? ` ${titleSuffix}` : '';
    return `${titlePrefix} ${userName}${suffixPart}`;
  };

  // 載入歷史記錄
  const loadRoomHistory = () => {
    try {
      const saved = localStorage.getItem('vcaas_room_history');
      if (saved) {
        const history = JSON.parse(saved);
        setRoomHistory(history.slice(0, 5)); // 只保留最近 5 個
      }
    } catch (error) {
      console.error('載入歷史記錄失敗:', error);
    }
  };

  // 保存新建教室到歷史記錄
  const saveToHistory = (room: VirtualRoomResponse) => {
    try {
      const saved = localStorage.getItem('vcaas_room_history');
      let history: VirtualRoomResponse[] = saved ? JSON.parse(saved) : [];

      // 添加新記錄到開頭
      history.unshift(room);

      // 只保留最近 10 個記錄
      history = history.slice(0, 10);

      localStorage.setItem('vcaas_room_history', JSON.stringify(history));
      setRoomHistory(history.slice(0, 5)); // 顯示最近 5 個
    } catch (error) {
      console.error('保存歷史記錄失敗:', error);
    }
  };

  // 清除歷史記錄
  const clearHistory = () => {
    localStorage.removeItem('vcaas_room_history');
    setRoomHistory([]);
  };

  // 從 link 中提取 token
  const extractToken = (link: string): string | null => {
    try {
      const url = new URL(link);
      return url.searchParams.get('token');
    } catch {
      return null;
    }
  };

  // 生成 .cn 域名鏈接
  const getCnLink = (originalLink: string): string => {
    return originalLink.replace('.com', '.cn');
  };

  // 獲取環境對應的 Class URL
  const getClassUrl = (environment: string): string => {
    const urlMap: Record<string, string> = {
      'dev': 'https://class.vcaas-dev.amazingtalker.com',
      'staging': 'https://class.vcaas-staging.amazingtalker.com',
      'uat': 'https://class.vcaas-uat.amazingtalker.com',
      'production': 'https://class.vcaas.amazingtalker.com',
    };
    return urlMap[environment] || urlMap['dev'];
  };

  // 複製到剪貼板
  // 複製到剪貼板
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setToast({ message: `已複製 ${label}`, type: 'success' });
    }).catch(() => {
      setToast({ message: '複製失敗', type: 'error' });
    });
  };

  // 處理複製連結（包含自動生成）
  const handleCopyLinks = async (room: VirtualRoomResponse, type: 'com' | 'cn' | 'redirect' | 'deeplink') => {
    let text = `ID: ${room.id}\n`;
    const links = room.links || [];

    if (type === 'com' || type === 'cn') {
      links.forEach(link => {
        let url = link.link;
        if (type === 'cn') {
          url = getCnLink(url);
        }
        text += `${link.role_display}: ${url}\n`;
      });
      copyToClipboard(text, type === 'com' ? '.com 連結' : '.cn 連結');
    } else {
      // 對於 redirect 和 deeplink，需要檢查並生成
      const account = room.account || formData.account || selectedAccount || '';
      if (!account) {
        setToast({ message: '無法複製：找不到帳號信息', type: 'error' });
        return;
      }

      try {
        const generatedLinks = await Promise.all(links.map(async (link) => {
          const key = `${room.id}_${link.role}_${type}`;
          let url = deepLinks[key];

          if (!url) {
            // 生成它
            const token = extractToken(link.link);
            if (token && room.environment) {
              const attendeeName = `Febe ${link.role_display}`;
              const joinToken = await generateDeepLink(token, attendeeName, room.environment, account);
              const classUrl = getClassUrl(room.environment);

              if (type === 'redirect') {
                const meetingUrl = `${classUrl}/meeting?joinToken=${joinToken}`;
                url = `${classUrl}/redirect-download?meetingRoomUrl=${encodeURIComponent(meetingUrl)}`;
              } else {
                url = `at-classroom://${new URL(classUrl).host}/meeting?joinToken=${joinToken}`;
              }

              // 更新狀態
              setDeepLinks(prev => ({ ...prev, [key]: url }));
            }
          }
          return { role: link.role_display, url };
        }));

        generatedLinks.forEach(item => {
          if (item.url) {
            text += `${item.role}: ${item.url}\n`;
          }
        });

        copyToClipboard(text, type === 'redirect' ? 'Web Redirect 連結' : 'Deep Link 連結');

      } catch (error: any) {
        console.error('生成連結失敗:', error);
        setToast({ message: `生成並複製連結失敗: ${error.message}`, type: 'error' });
      }
    }
  };

  // 重置配置為預設值
  const resetMeetingConfig = () => {
    setMeetingConfig({
      // 基礎通訊功能
      enableChat: true,
      enableRecording: true,
      enableFilePlayer: true,
      enableWhiteBoard: true,
      enableContentShare: true,

      // 互動工具
      enableQuiz: true,
      enablePoll: true,
      enableReaction: true,
      enableReward: true,

      // 多媒體工具
      enableYoutubePlayer: true,
      enableWebLinks: true,

      // 輔助工具
      enableCountdown: true,
      enableNameRandomizer: true,
      enableDice: true,
      enableCalculator: true,
      enableCodeEditor: false,

      // 第三方整合
      enableNearpod: true,

      // 進階功能
      enableBreakoutGroup: false,
      enableStudentScreenViewer: false,
      enableStudentSendChatFile: false,
      enablePostClassTranscription: false,
      enableVirtualBackground: true,
      enableAfterClassEval: true,
      enableWaitingRoom: false,

      // 錄影相關
      enableRecordingButton: true,
      enableAutoRecording: false,
      enableRecordingPermission: true,
      enableBrowserRecBanner: true,
      enablePrivacyRecording: false,
      recordingMethod: 'CLASSIC',
      recordingFileTypes: ['MP4'],

      // 課程管理
      enableLessonPlan: false,
      enableClassroomInvitation: true,
      enableEndButton: true,
      enableStartStopClass: false,
      enableSupportTicket: false,
      enableTeacherOnboarding: true,

      // 系統設定
      enableBrowserUpdatePrompt: false,
      defaultView: 'sidebar',
    });
  };

  // 計算已啟用的功能數量
  const getEnabledFeaturesCount = () => {
    return Object.entries(meetingConfig).filter(([key, value]) => {
      // 跳過非布林值的配置項
      if (key === 'recordingMethod' || key === 'recordingFileTypes' || key === 'defaultView') {
        return false;
      }
      return value === true;
    }).length;
  };


  // 分類配置定義
  const configCategories = [
    {
      id: 'core',
      name: '基礎通訊',
      icon: '💬',
      color: 'blue',
      count: 5,
      options: [
        { key: 'enableChat', label: '聊天', icon: '💬' },
        { key: 'enableRecording', label: '錄影', icon: '🎥' },
        { key: 'enableFilePlayer', label: '檔案', icon: '📁' },
        { key: 'enableWhiteBoard', label: '白板', icon: '📝' },
        { key: 'enableContentShare', label: '分享', icon: '📺' },
      ]
    },
    {
      id: 'interactive',
      name: '互動工具',
      icon: '🎯',
      color: 'green',
      count: 4,
      options: [
        { key: 'enableQuiz', label: '測驗', icon: '❓' },
        { key: 'enablePoll', label: '投票', icon: '📊' },
        { key: 'enableReaction', label: '反應', icon: '😀' },
        { key: 'enableReward', label: '獎勵', icon: '🏆' },
      ]
    },
    {
      id: 'media',
      name: '多媒體',
      icon: '📺',
      color: 'purple',
      count: 2,
      options: [
        { key: 'enableYoutubePlayer', label: 'YouTube', icon: '📺' },
        { key: 'enableWebLinks', label: '連結', icon: '🔗' },
      ]
    },
    {
      id: 'tools',
      name: '輔助工具',
      icon: '🛠️',
      color: 'orange',
      count: 5,
      options: [
        { key: 'enableCountdown', label: '計時', icon: '⏰' },
        { key: 'enableNameRandomizer', label: '選名', icon: '🎲' },
        { key: 'enableDice', label: '骰子', icon: '🎯' },
        { key: 'enableCalculator', label: '計算', icon: '🧮' },
        { key: 'enableCodeEditor', label: '編程', icon: '💻' },
      ]
    },
    {
      id: 'integration',
      name: '第三方',
      icon: '🔗',
      color: 'indigo',
      count: 1,
      options: [
        { key: 'enableNearpod', label: 'Nearpod', icon: '🎯' },
      ]
    },
    {
      id: 'advanced',
      name: '進階功能',
      icon: '⚡',
      color: 'red',
      count: 7,
      options: [
        { key: 'enableBreakoutGroup', label: '分組', icon: '👥' },
        { key: 'enableStudentScreenViewer', label: '螢幕檢視', icon: '👁️' },
        { key: 'enableStudentSendChatFile', label: '發送檔案', icon: '📎' },
        { key: 'enablePostClassTranscription', label: '轉錄', icon: '📝' },
        { key: 'enableVirtualBackground', label: '背景', icon: '🖼️' },
        { key: 'enableAfterClassEval', label: '評量', icon: '⭐' },
        { key: 'enableWaitingRoom', label: '等候室', icon: '⏳' },
      ]
    },
    {
      id: 'recording',
      name: '錄影相關',
      icon: '🎬',
      color: 'pink',
      count: 5,
      options: [
        { key: 'enableRecordingButton', label: '錄影按鈕', icon: '🔴' },
        { key: 'enableAutoRecording', label: '自動錄影', icon: '🤖' },
        { key: 'enableRecordingPermission', label: '錄影權限', icon: '🔐' },
        { key: 'enableBrowserRecBanner', label: '錄影橫幅', icon: '🏷️' },
        { key: 'enablePrivacyRecording', label: '隱私錄影', icon: '🔒' },
      ]
    },
    {
      id: 'management',
      name: '課程管理',
      icon: '📚',
      color: 'teal',
      count: 6,
      options: [
        { key: 'enableLessonPlan', label: '課程計劃', icon: '📋' },
        { key: 'enableClassroomInvitation', label: '教室邀請', icon: '📧' },
        { key: 'enableEndButton', label: '結束按鈕', icon: '🛑' },
        { key: 'enableStartStopClass', label: '開始停止', icon: '▶️' },
        { key: 'enableSupportTicket', label: '支援票券', icon: '🎫' },
        { key: 'enableTeacherOnboarding', label: '教師引導', icon: '👨‍🏫' },
      ]
    },
    {
      id: 'system',
      name: '系統設定',
      icon: '⚙️',
      color: 'gray',
      count: 1,
      options: [
        { key: 'enableBrowserUpdatePrompt', label: '更新提示', icon: '🔄' },
      ]
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // 使用預覽標題作為最終標題（包含前綴）
      const finalTitle = getFinalTitlePreview();

      const requestData = {
        ...formData,
        name: finalTitle,
        meetingConfig: meetingConfig,
      };

      const response = await createVirtualRoom(requestData);
      if (response.success && response.data) {
        setResult(response.data);
        saveToHistory(response.data); // 保存到歷史記錄
      } else {
        setError(response.error || '創建虛擬教室失敗');
      }
    } catch (err: any) {
      setError(err.message || '請求失敗');
    } finally {
      setLoading(false);
    }
  };



  const handleDurationButtonClick = (duration: number) => {
    setFormData(prev => ({ ...prev, duration }));
  };

  const environmentDisplay: { [key: string]: string } = {
    dev: '🟢 DEV - 開發環境',
    staging: '🟡 STAGING - 預發布環境',
    uat: '🟠 UAT - 用戶驗收測試環境',
    production: '🔴 PRODUCTION - 正式環境',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* 頂部導航欄 */}
      <div className="bg-white/80 backdrop-blur-md shadow-sm border-b border-white/20 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl">🎯</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">VCaaS 教室自動化工具</h1>
                <p className="text-sm text-gray-500">快速建立不同環境和配置的虛擬教室</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">系統正常</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
          <div className={`px-6 py-3 rounded-full shadow-lg flex items-center space-x-2 ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}>
            <span>{toast.type === 'success' ? '✅' : '❌'}</span>
            <span className="font-medium text-sm">{toast.message}</span>
          </div>
        </div>
      )}

      {/* 主要內容區域 */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* 主要表單 */}
          <div className="xl:col-span-3">
            <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              {/* 表單標題 */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">建立新教室</h2>
                    <p className="text-blue-100 text-sm">配置您的虛擬教室環境</p>
                  </div>
                </div>
              </div>

              {/* 錯誤訊息 */}
              {error && (
                <div className="mx-8 mt-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-xl">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">發生錯誤</p>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 表單內容 */}
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 1. 環境配置 */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">環境配置</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">部署環境</label>
                        <select
                          value={formData.environment}
                          onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value as any }))}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-gray-700"
                        >
                          {environments.length === 0 ? (
                            <option>正在加載環境...</option>
                          ) : (
                            environments.map(env => (
                              <option key={env} value={env}>{environmentDisplay[env] || env}</option>
                            ))
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">測試帳號</label>
                        <select
                          value={formData.account || ''}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, account: e.target.value }));
                            setSelectedAccount(e.target.value);
                          }}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-gray-700"
                        >
                          {accounts.length === 0 ? (
                            <option value="">正在加載帳號...</option>
                          ) : (
                            accounts.map(account => (
                              <option key={account.key} value={account.key}>
                                👤 {account.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* 2. 基本設定（標題 + 時間） */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-100">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">基本設定</h3>
                    </div>

                    <div className="space-y-4">
                      {/* 標題設定 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">會議標題</label>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium text-gray-800 whitespace-nowrap self-start sm:self-auto">{titlePrefix}</span>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            disabled
                            className="w-auto px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 cursor-not-allowed text-center"
                            placeholder="名稱"
                            style={{ minWidth: '60px', maxWidth: '150px' }}
                          />
                          <input
                            type="text"
                            value={titleSuffix}
                            onChange={(e) => setTitleSuffix(e.target.value)}
                            placeholder="後綴（可編輯）"
                            className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-gray-700 text-sm"
                          />
                        </div>
                      </div>

                      {/* 時間設定 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">會議時間</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <select
                              value={formData.timePreset}
                              onChange={(e) => setFormData(prev => ({ ...prev, timePreset: e.target.value as any }))}
                              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-gray-700 text-sm"
                            >
                              <option value="now">立即開始</option>
                              <option value="11:30">今日11:30</option>
                            </select>
                          </div>
                          <div>
                            <input
                              type="number"
                              value={formData.duration}
                              onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                              min="5"
                              max="480"
                              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-gray-700 text-sm"
                              placeholder="時長（分鐘）"
                            />
                          </div>
                        </div>
                        <div className="space-y-2 mt-2">
                          {/* 常用時長 */}
                          <div className="flex space-x-1">
                            {[15, 30, 60, 90].map(duration => (
                              <button
                                key={duration}
                                type="button"
                                onClick={() => handleDurationButtonClick(duration)}
                                className={`px-2 py-1 border rounded text-xs transition-all ${formData.duration === duration ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-700 border-gray-300 hover:border-green-300'}`}
                              >
                                {duration}min
                              </button>
                            ))}
                          </div>

                          {/* 快捷場景 */}
                          <div className="flex space-x-1">
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timePreset: 'now', duration: 15 }));
                                setTitleSuffix('');
                              }}
                              className="px-2 py-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded text-xs text-blue-700 transition-all"
                            >
                              ⚡ 快速測試
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timePreset: 'now', duration: 60 }));
                                setTitleSuffix('Demo');
                              }}
                              className="px-2 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded text-xs text-purple-700 transition-all"
                            >
                              🎯 演示會議
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 建立教室按鈕 */}
                    <div className="mt-4 pt-4 border-t border-yellow-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin mr-3 text-lg">🔄</span>
                            建立教室中...
                          </>
                        ) : (
                          <>
                            <span className="mr-3 text-lg">🚀</span>
                            建立教室
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* 3. 功能配置 */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">功能配置</h3>
                          <p className="text-sm text-gray-600">選擇虛擬教室的功能選項（已啟用 {getEnabledFeaturesCount()} 項）</p>
                        </div>
                        <button
                          type="button"
                          onClick={resetMeetingConfig}
                          className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center"
                        >
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          重置預設
                        </button>
                      </div>
                    </div>

                    {/* 功能配置 - 簡潔按鈕式設計 */}
                    <div className="space-y-4">
                      {configCategories.map((category) => (
                        <div key={category.id} className="space-y-2">
                          {/* 分類標題 */}
                          <div className="flex items-center space-x-2">
                            <span className="text-base">{category.icon}</span>
                            <h4 className="font-medium text-gray-800 text-sm">{category.name}</h4>
                            <div className="text-xs text-gray-500">
                              ({category.options.filter(option => meetingConfig[option.key as keyof BytePlusConfig] === true).length}/{category.count})
                            </div>
                          </div>

                          {/* 選項按鈕網格 */}
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {category.options.map((option) => {
                              const isSelected = meetingConfig[option.key as keyof BytePlusConfig] === true;

                              // 預定義的顏色類別
                              const selectedColors = {
                                blue: 'bg-blue-100 border-blue-300 text-blue-800',
                                green: 'bg-green-100 border-green-300 text-green-800',
                                purple: 'bg-purple-100 border-purple-300 text-purple-800',
                                orange: 'bg-orange-100 border-orange-300 text-orange-800',
                                indigo: 'bg-indigo-100 border-indigo-300 text-indigo-800',
                                red: 'bg-red-100 border-red-300 text-red-800',
                                pink: 'bg-pink-100 border-pink-300 text-pink-800',
                                teal: 'bg-teal-100 border-teal-300 text-teal-800',
                                gray: 'bg-gray-100 border-gray-300 text-gray-800',
                              };

                              return (
                                <button
                                  key={option.key}
                                  type="button"
                                  onClick={() => setMeetingConfig(prev => ({
                                    ...prev,
                                    [option.key]: !prev[option.key as keyof BytePlusConfig]
                                  }))}
                                  className={`p-2 rounded-lg border transition-all duration-200 text-left ${isSelected
                                    ? selectedColors[category.color as keyof typeof selectedColors]
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                                    }`}
                                >
                                  <div className="flex items-center space-x-1">
                                    <span className="text-sm">{option.icon}</span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-xs font-medium truncate">
                                        {option.label}
                                      </div>
                                      <div className="text-xs opacity-60 truncate">
                                        {option.key}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* 右側邊欄 */}
          <div className="space-y-6">
            {/* 即時狀態卡片 */}
            <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-white">即時狀態</h3>
                    <p className="text-emerald-100 text-xs">當前配置概覽</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">環境</span>
                    </div>
                    <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                      {formData.environment.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-4 rounded-xl border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">帳號</span>
                    </div>
                    <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full truncate max-w-24">
                      {accounts.find(acc => acc.key === selectedAccount)?.name.split(' ')[0] || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">配置</span>
                    </div>
                    <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                      {configType}
                    </span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700">預覽標題</span>
                  </div>
                  <p className="text-sm font-bold text-gray-800 bg-white p-3 rounded-lg border">{getFinalTitlePreview()}</p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-100 p-4 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">啟用功能</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                        {getEnabledFeaturesCount()} 項
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 歷史記錄卡片 */}
            <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-white">建立紀錄</h3>
                      <p className="text-indigo-100 text-xs">最近建立的教室</p>
                    </div>
                  </div>
                  {roomHistory.length > 0 && (
                    <button
                      onClick={clearHistory}
                      className="text-white/70 hover:text-white text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-all"
                    >
                      清除
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4">
                {roomHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-sm">尚無建立記錄</p>
                    <p className="text-xs text-gray-400 mt-1">建立首個教室後將顯示於此</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {roomHistory.map((room) => (
                      <div
                        key={room.id}
                        className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg hover:shadow-md transition-all group"
                      >
                        {/* 名稱和資訊 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {room.name}
                            </span>
                            <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-xs font-mono rounded">
                              {room.environment?.toUpperCase()}
                            </span>
                          </div>
                          {/* 第一行：日期 */}
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-500">
                              {new Date(room.createdAt).toLocaleString('zh-TW', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {/* 第二行：ID */}
                          <div className="flex items-center space-x-2 mt-1">
                            <code className="text-xs text-gray-500 font-mono select-all">ID: {room.id}</code>
                          </div>
                        </div>

                        {/* .com 連結 */}
                        <div className="mt-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => handleCopyLinks(room, 'com')}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="複製所有 .com 連結"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <div className="text-xs text-gray-500">.com:</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {room.links?.map(link => (
                              <a
                                key={link.role}
                                href={link.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold rounded-full transition-colors shadow-sm"
                                title={`以 ${link.role_display} 身份進入 (.com)`}
                              >
                                {link.role_display.substring(0, 1)}
                              </a>
                            ))}
                            <a
                              href={room.adminUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center w-6 h-6 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-full transition-colors shadow-sm"
                              title="管理頁面"
                            >
                              ⚙️
                            </a>
                          </div>
                        </div>

                        {/* .cn 連結 */}
                        <div className="mt-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => handleCopyLinks(room, 'cn')}
                              className="text-gray-400 hover:text-purple-600 transition-colors"
                              title="複製所有 .cn 連結"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <div className="text-xs text-gray-500">.cn:</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {room.links?.map(link => (
                              <a
                                key={`cn_${link.role}`}
                                href={getCnLink(link.link)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center w-6 h-6 bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold rounded-full transition-colors shadow-sm"
                                title={`以 ${link.role_display} 身份進入 (.cn)`}
                              >
                                {link.role_display.substring(0, 1)}
                              </a>
                            ))}
                          </div>
                        </div>

                        {/* Web Redirect Download */}
                        <div className="mt-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => handleCopyLinks(room, 'redirect')}
                              className="text-gray-400 hover:text-emerald-600 transition-colors"
                              title="複製所有 Web Redirect 連結"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <div className="text-xs text-gray-500">Web Redirect:</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {room.links?.map(link => {
                              const token = extractToken(link.link);
                              const key = `${room.id}_${link.role}_redirect`;
                              const isLoading = loadingDeepLinks[key];
                              const deepLinkUrl = deepLinks[key];

                              return (
                                <button
                                  key={`redirect_${link.role}`}
                                  onClick={async () => {
                                    if (token && room.environment && room.account) {
                                      // 如果已經生成過，直接打開
                                      if (deepLinkUrl) {
                                        window.open(deepLinkUrl, '_blank');
                                        return;
                                      }

                                      // 否則生成並打開
                                      setLoadingDeepLinks(prev => ({ ...prev, [key]: true }));

                                      try {
                                        const attendeeName = `Febe ${link.role_display}`;
                                        const joinToken = await generateDeepLink(token, attendeeName, room.environment, room.account);

                                        const classUrl = getClassUrl(room.environment);
                                        const meetingUrl = `${classUrl}/meeting?joinToken=${joinToken}`;
                                        const redirectUrl = `${classUrl}/redirect-download?meetingRoomUrl=${encodeURIComponent(meetingUrl)}`;

                                        setDeepLinks(prev => ({ ...prev, [key]: redirectUrl }));
                                        window.open(redirectUrl, '_blank');
                                      } catch (error: any) {
                                        console.error('生成連結失敗:', error);
                                        alert(`生成連結失敗: ${error.message}`);
                                      } finally {
                                        setLoadingDeepLinks(prev => ({ ...prev, [key]: false }));
                                      }
                                    }
                                  }}
                                  disabled={isLoading || !token}
                                  className={`flex items-center justify-center w-6 h-6 text-white text-xs font-bold rounded-full transition-colors shadow-sm ${isLoading ? 'bg-gray-400 cursor-wait' : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                  title={`${link.role_display} Web Redirect`}
                                >
                                  {isLoading ? '⏳' : link.role_display.substring(0, 1)}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Deep Link */}
                        <div className="mt-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => handleCopyLinks(room, 'deeplink')}
                              className="text-gray-400 hover:text-orange-600 transition-colors"
                              title="複製所有 Deep Link"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <div className="text-xs text-gray-500">Deep Link:</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {room.links?.map(link => {
                              const token = extractToken(link.link);
                              const key = `${room.id}_${link.role}_deeplink`;
                              const isLoading = loadingDeepLinks[key];
                              const deepLinkUrl = deepLinks[key];

                              return (
                                <button
                                  key={`deeplink_${link.role}`}
                                  onClick={async () => {
                                    if (token && room.environment && room.account) {
                                      // 如果已經生成過，直接打開
                                      if (deepLinkUrl) {
                                        window.open(deepLinkUrl, '_blank');
                                        return;
                                      }

                                      // 否則生成並打開
                                      setLoadingDeepLinks(prev => ({ ...prev, [key]: true }));

                                      try {
                                        const attendeeName = `Febe ${link.role_display}`;
                                        const joinToken = await generateDeepLink(token, attendeeName, room.environment, room.account);

                                        const classUrl = getClassUrl(room.environment);
                                        const deepLink = `at-classroom://${new URL(classUrl).host}/meeting?joinToken=${joinToken}`;

                                        setDeepLinks(prev => ({ ...prev, [key]: deepLink }));
                                        window.open(deepLink, '_blank');
                                      } catch (error: any) {
                                        console.error('生成 Deep Link 失敗:', error);
                                        alert(`生成 Deep Link 失敗: ${error.message}`);
                                      } finally {
                                        setLoadingDeepLinks(prev => ({ ...prev, [key]: false }));
                                      }
                                    }
                                  }}
                                  disabled={isLoading || !token}
                                  className={`flex items-center justify-center w-6 h-6 text-white text-xs font-bold rounded-full transition-colors shadow-sm ${isLoading ? 'bg-gray-400 cursor-wait' : 'bg-orange-500 hover:bg-orange-600'
                                    }`}
                                  title={`${link.role_display} Deep Link`}
                                >
                                  {isLoading ? '⏳' : link.role_display.substring(0, 1)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 結果顯示區域 - 改為頂部通知樣式 */}
        {result && (
          <div className="fixed top-4 right-4 left-4 z-50 max-w-2xl ml-auto">
            <div className="bg-white border-l-4 border-green-500 rounded-lg shadow-2xl overflow-hidden animate-bounce-in">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      教室建立成功！
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {result.name} 已在 {result.environment?.toUpperCase()} 環境中建立
                    </p>

                    {/* 教室 ID 和管理頁面 */}
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600">教室 ID:</span>
                        <code className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded font-mono">{result.id}</code>
                        <button
                          onClick={() => copyToClipboard(result.id, '教室 ID')}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="複製教室 ID"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>

                      <a
                        href={result.adminUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1.5 px-3.5 py-2 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>管理頁面</span>
                      </a>
                    </div>

                    {/* .com 進入連結 */}
                    <div className="mt-4">
                      <div className="flex items-center space-x-2 mb-2.5">
                        <button
                          onClick={() => handleCopyLinks(result, 'com')}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="複製所有 .com 連結"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <p className="text-xs font-semibold text-gray-700">.com 進入連結</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {result.links?.map((link) => (
                          <a
                            key={link.role}
                            href={link.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center space-x-1.5 px-3.5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>{link.role_display}</span>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* .cn 進入連結 */}
                    <div className="mt-3.5">
                      <div className="flex items-center space-x-2 mb-2.5">
                        <button
                          onClick={() => handleCopyLinks(result, 'cn')}
                          className="text-gray-400 hover:text-purple-600 transition-colors"
                          title="複製所有 .cn 連結"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <p className="text-xs font-semibold text-gray-700">.cn 進入連結</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {result.links?.map((link) => (
                          <a
                            key={`cn_${link.role}`}
                            href={getCnLink(link.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center space-x-1.5 px-3.5 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{link.role_display}</span>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Web Redirect Download */}
                    <div className="mt-3.5">
                      <div className="flex items-center space-x-2 mb-2.5">
                        <button
                          onClick={() => handleCopyLinks(result, 'redirect')}
                          className="text-gray-400 hover:text-emerald-600 transition-colors"
                          title="複製所有 Web Redirect 連結"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <p className="text-xs font-semibold text-gray-700">Web Redirect Download</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {result.links?.map((link) => {
                          const token = extractToken(link.link);
                          const key = `${result.id}_${link.role}_redirect`;
                          const isLoading = loadingDeepLinks[key];
                          const redirectUrl = deepLinks[key];

                          return (
                            <button
                              key={`redirect_${link.role}`}
                              onClick={async () => {
                                if (token && result.environment) {
                                  // 如果已經生成過，直接打開
                                  if (redirectUrl) {
                                    window.open(redirectUrl, '_blank');
                                    return;
                                  }

                                  setLoadingDeepLinks(prev => ({ ...prev, [key]: true }));
                                  try {
                                    const attendeeName = `Febe ${link.role_display}`;
                                    const account = formData.account || selectedAccount || '';

                                    if (!account) {
                                      throw new Error('找不到帳號信息');
                                    }

                                    const joinToken = await generateDeepLink(token, attendeeName, result.environment, account);
                                    const classUrl = getClassUrl(result.environment);
                                    const meetingUrl = `${classUrl}/meeting?joinToken=${joinToken}`;
                                    const webRedirectUrl = `${classUrl}/redirect-download?meetingRoomUrl=${encodeURIComponent(meetingUrl)}`;

                                    setDeepLinks(prev => ({ ...prev, [key]: webRedirectUrl }));
                                    window.open(webRedirectUrl, '_blank');
                                  } catch (error: any) {
                                    console.error('生成 Web Redirect 連結失敗:', error);
                                    alert(`生成 Web Redirect 連結失敗: ${error.message}`);
                                  } finally {
                                    setLoadingDeepLinks(prev => ({ ...prev, [key]: false }));
                                  }
                                }
                              }}
                              disabled={isLoading || !token}
                              className={`group inline-flex items-center space-x-1.5 px-3.5 py-2 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${isLoading
                                ? 'bg-gray-400 cursor-wait'
                                : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                                }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isLoading ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                )}
                              </svg>
                              <span>{link.role_display}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Deep Link */}
                    <div className="mt-3.5">
                      <div className="flex items-center space-x-2 mb-2.5">
                        <button
                          onClick={() => handleCopyLinks(result, 'deeplink')}
                          className="text-gray-400 hover:text-orange-600 transition-colors"
                          title="複製所有 Deep Link"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <p className="text-xs font-semibold text-gray-700">Deep Link</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {result.links?.map((link) => {
                          const token = extractToken(link.link);
                          const key = `${result.id}_${link.role}_deeplink`;
                          const isLoading = loadingDeepLinks[key];

                          return (
                            <button
                              key={`deeplink_${link.role}`}
                              onClick={async () => {
                                if (token && result.environment) {
                                  setLoadingDeepLinks(prev => ({ ...prev, [key]: true }));
                                  try {
                                    const attendeeName = `Febe ${link.role_display}`;
                                    const account = formData.account || selectedAccount || '';

                                    if (!account) {
                                      throw new Error('找不到帳號信息');
                                    }

                                    const joinToken = await generateDeepLink(token, attendeeName, result.environment, account);
                                    const classUrl = getClassUrl(result.environment);
                                    const deepLink = `at-classroom://${new URL(classUrl).host}/meeting?joinToken=${joinToken}`;

                                    setDeepLinks(prev => ({ ...prev, [key]: deepLink }));
                                    window.open(deepLink, '_blank');
                                  } catch (error: any) {
                                    console.error('生成 Deep Link 失敗:', error);
                                    alert(`生成 Deep Link 失敗: ${error.message}`);
                                  } finally {
                                    setLoadingDeepLinks(prev => ({ ...prev, [key]: false }));
                                  }
                                }
                              }}
                              disabled={isLoading || !token}
                              className={`group inline-flex items-center space-x-1.5 px-3.5 py-2 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${isLoading
                                ? 'bg-gray-400 cursor-wait'
                                : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                                }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isLoading ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                )}
                              </svg>
                              <span>{link.role_display}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => setResult(null)}
                      className="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualRoomCreator;
